import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, MapPin } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | timeout | error
  const polls = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await checkAuth();
          setState("success");
          setTimeout(() => navigate("/dashboard", { replace: true }), 1600);
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [navigate, checkAuth]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="text-center max-w-md" data-testid="payment-success-page">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="h-8 w-8 rounded-md bg-[#0055FF] flex items-center justify-center"><MapPin weight="fill" size={18} className="text-white" /></div>
          <span className="font-display font-black text-lg tracking-tight text-slate-900">CiteHub</span>
        </div>

        {state === "checking" && (
          <>
            <div className="h-12 w-12 mx-auto rounded-full border-2 border-slate-200 border-t-[#0055FF] animate-spin" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-6">Confirming your payment…</h1>
            <p className="text-slate-600 mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle weight="fill" size={64} className="text-emerald-500 mx-auto" />
            <h1 className="font-display text-3xl font-black tracking-tight text-slate-900 mt-6">You're all set!</h1>
            <p className="text-slate-600 mt-2">Membership active. Taking you to your dashboard…</p>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle weight="fill" size={64} className="text-amber-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-6">
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-slate-600 mt-2">
              {state === "timeout" ? "Your payment is taking longer than usual. Check your dashboard shortly." : "We couldn't confirm your payment."}
            </p>
            <Button onClick={() => navigate("/dashboard")} data-testid="success-continue-button" className="mt-6 bg-[#0055FF] hover:bg-[#0044CC] text-white">Go to dashboard</Button>
          </>
        )}
      </div>
    </div>
  );
}
